import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Helmet } from "react-helmet-async";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CancelIcon from "@mui/icons-material/Cancel";
import CheckIcon from '@mui/icons-material/Check';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from "../../config/axios";

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [monthlyData, setMonthlyData] = useState([]);
  const [expenseData, setExpenseData] = useState([]);
  const [processedExpenseData, setProcessedExpenseData] = useState([]);
  const [weekdayData, setWeekdayData] = useState([]);

  const currentYear = new Date().getFullYear();
  const startYear = 2025;
  const availableYears = [];
  for (let y = startYear; y <= Math.max(startYear, currentYear); y++) {
    availableYears.push(y);
  }
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [showProfit, setShowProfit] = useState(false);
  const [showUnseenAddressNotification, setShowUnseenAddressNotification] = useState(false);
  const [unseenAddresses, setUnseenAddresses] = useState([]);
  const [acknowledging, setAcknowledging] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const [statsRef, statsInView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const [metricsRef, metricsInView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });



  const handleStatusCardClick = (status) => {
    if (status) {
      navigate(`/orders?status=${status}`);
    }
  };

  const handleUnseenAddressOkay = () => {
    setShowUnseenAddressNotification(false);
  };

  const fetchUnseenAddresses = async () => {
    try {
      const res = await axios.get("/get_unseen_addresses.php", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res?.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setUnseenAddresses(res.data.data);
        setShowUnseenAddressNotification(true);
      } else {
        setUnseenAddresses([]);
      }
    } catch (e) {
      setUnseenAddresses([]);
    }
  };

  const handleAcknowledgeAll = async () => {
    setAcknowledging(true);
    try {
      await axios.post("/acknowledge_addresses.php", {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setUnseenAddresses([]);
      setShowUnseenAddressNotification(false);
    } catch (e) {
      // keep modal open on error; could show toast
    } finally {
      setAcknowledging(false);
    }
  };

  const fetchMonthlyData = async (year) => {
    const yearToFetch = year !== undefined ? year : selectedYear;
    try {
      const res = await axios.get("/graph_data.php", {
        params: { year: yearToFetch },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res?.data?.success && Array.isArray(res.data.data)) {
        // Exclude months with 0 orders per user request
        const filtered = res.data.data.filter((item) => (item.orders || 0) > 0);
        setMonthlyData(filtered);
      } else {
        setMonthlyData([]);
      }
    } catch (error) {
      console.error("Error fetching monthly data:", error);
      setMonthlyData([]);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    fetchMonthlyData(year);
  };

  const fetchExpenseData = async () => {
    try {
      const res = await axios.get("/expense_graph_data.php", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res?.data?.success && Array.isArray(res.data.data)) {
        setExpenseData(res.data.data);
      } else {
        setExpenseData([]);
      }
    } catch (error) {
      console.error("Error fetching expense data:", error);
      setExpenseData([]);
    }
  };

  const fetchWeekdayData = async () => {
    try {
      const res = await axios.get("/weekday_orders_data.php", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res?.data?.success && Array.isArray(res.data.data)) {
        setWeekdayData(res.data.data);
      } else {
        setWeekdayData([]);
      }
    } catch (error) {
      console.error("Error fetching weekday data:", error);
      setWeekdayData([]);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchMonthlyData(currentYear);
    fetchExpenseData();
    fetchWeekdayData();
    fetchUnseenAddresses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get("/orders_status_counts.php", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const mockStats = {
        totalOrders: response.data.data.total,
        pendingApprovalOrders: response.data.data["pending-approval"] || 0,
        acceptedOrders: response.data.data["accepted"],
        inProgressOrders: response.data.data["in-progress"],
        dispatchedOrders: response.data.data["dispatched"],
        deliveredOrders: response.data.data["delivered"],
        canceledOrders: response.data.data["canceled"],
        completedOrders: response.data.data["completed"],
        readyToShipOrders: response.data.data["ready-to-ship"],
      };

      setStats(mockStats);
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };
  const orderStatusCards = [
    {
      title: "Total Orders",
      value: stats?.totalOrders || 0,
      icon: <ShoppingCartIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      fillColor: "rgba(67, 233, 123, 1)",
      status: null,
    },
    {
      title: "Pending Approval",
      value: stats?.pendingApprovalOrders || 0,
      icon: <CheckCircleOutlineIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "rgb(199, 33, 66)",
      fillColor: "rgba(255, 152, 0, 1)",
      percentage: ((stats?.pendingApprovalOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "pending-approval",
    },
    {
      title: "Accepted",
      value: stats?.acceptedOrders || 0,
      icon: <CheckIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
      fillColor: "rgba(21, 101, 192, 1)",
      percentage: ((stats?.acceptedOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "accepted",
    },
    {
      title: "In Progress",
      value: stats?.inProgressOrders || 0,
      icon: <PendingIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #f57c00 0%, #ef6c00 100%)",
      fillColor: "rgba(245, 124, 0, 1)",
      percentage: ((stats?.inProgressOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "in-progress",
    },
    {
      title: "Ready to Ship",
      value: stats?.readyToShipOrders || 0,
      icon: <AddShoppingCartIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)",
      fillColor: "rgba(56, 142, 60, 1)",
      percentage: ((stats?.readyToShipOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "ready-to-ship",
    },
    {
      title: "Dispatched",
      value: stats?.dispatchedOrders || 0,
      icon: <LocalShippingIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)",
      fillColor: "rgba(123, 31, 162, 1)",
      percentage: ((stats?.dispatchedOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "dispatched",
    },
    {
      title: "Delivered",
      value: stats?.deliveredOrders || 0,
      icon: <CheckCircleIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #00695c 0%, #004d40 100%)",
      fillColor: "rgba(0, 105, 92, 1)",
      percentage: ((stats?.deliveredOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "delivered",
    },
    {
      title: "Canceled",
      value: stats?.canceledOrders || 0,
      icon: <CancelIcon sx={{ fontSize: { xs: 28, md: 32 } }} />,
      color: "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)",
      fillColor: "rgba(198, 40, 40, 0.4)",
      percentage: ((stats?.canceledOrders || 0) / (stats?.totalOrders || 1)) * 100,
      status: "canceled",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", flexDirection: "column", gap: 2 }}>
        <CircularProgress size={60} sx={{ color: "primary.main" }} />
        <Typography variant="body1" sx={{ color: "text.secondary" }}>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - Ishqnama Art</title>
        <meta name="description" content="View your business statistics and orders on the Ishqnama Art dashboard." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box sx={{ width: "100%", background: '#FAF9F6', py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            {error && (
              <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                <Alert severity="error" sx={{ mb: { xs: 2, md: 4 }, borderRadius: 2 }}>{error}</Alert>
              </motion.div>
            )}

            {/* Unseen address notification */}
            <Dialog open={showUnseenAddressNotification} onClose={handleUnseenAddressOkay} PaperProps={{ sx: { borderRadius: 4, minWidth: { xs: "90%", sm: 440 }, maxWidth: 500, maxHeight: "90vh", p: 1 } }}>
              <DialogTitle sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "1.5rem", display: "flex", alignItems: "center", gap: 1.5, pb: 2 }}>
                <LocationOnIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <span>New Address Reviews ({unseenAddresses.length})</span>
              </DialogTitle>
              <DialogContent sx={{ px: 2, pt: 1.5, pb: 0 }}>
                <Box sx={{ height: 320, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {unseenAddresses.map((addr, idx) => (
                    <Box key={addr.id ?? idx} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, bgcolor: "background.paper", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700}>Order: {addr.display_order_id ?? addr.order_id}</Typography>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, py: 0.25, fontSize: "0.7rem" }} onClick={() => navigate(`/orders/${addr.order_id}`)}>View order</Button>
                      </Box>
                      {(addr.display_bride_name || addr.display_groom_name) && (
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>{[addr.display_bride_name, addr.display_groom_name].filter(Boolean).join(" & ")} have submitted the address.</Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">{addr.street_address}</Typography>
                      <Typography variant="body2" color="text.secondary">{[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}</Typography>
                    </Box>
                  ))}
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 3, gap: 1.5 }}>
                <Button variant="outlined" onClick={handleUnseenAddressOkay} sx={{ borderRadius: '12px', fontWeight: 700, color: 'text.secondary' }}>Later</Button>
                <Button variant="contained" onClick={handleAcknowledgeAll} disabled={acknowledging} sx={{ background: '#B51D6E', color: "#FFFFFF !important", borderRadius: '12px', fontWeight: 900, px: 3 }}>Acknowledge All</Button>
              </DialogActions>
            </Dialog>

            {/* Order Status Metrics */}
            <motion.div
              ref={metricsRef}
              variants={containerVariants}
              initial="hidden"
              animate={metricsInView ? "visible" : "hidden"}
            >
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: 900,
                  color: "text.primary",
                  mb: 3,
                  fontSize: { xs: "1.25rem", md: "1.5rem" },
                }}
              >
                Order Status
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(4, 1fr)",
                    md: "repeat(8, 1fr)",
                    lg: "repeat(8, 1fr)",
                  },
                  gap: { xs: 1.5, sm: 2, md: 1.5, lg: 2 },
                  mb: { xs: 3, md: 4 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                {orderStatusCards.map((card, index) => (
                  <motion.div
                    key={index}
                    variants={cardVariants}
                  >
                    <Card
                      onClick={() => handleStatusCardClick(card.status)}
                      sx={{
                        height: "100%",
                        background: "#FFFFFF",
                        border: "1px solid rgba(0, 0, 0, 0.05)",
                        borderRadius: 3,
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: card.status ? "pointer" : "default",
                        position: "relative",
                        overflow: "hidden",
                        "&:hover": {
                          transform: { xs: "none", md: "translateY(-4px)" },
                          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                          "& .status-icon-bg": {
                            transform: "scale(1.1)",
                          }
                        },
                      }}
                    >
                      <CardContent sx={{
                        p: 2.5,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}>
                        <Box
                          className="status-icon-bg"
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: "12px",
                            backgroundColor: card.fillColor, // Always colored as requested
                            color: "white", // High contrast icon
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1.5,
                            transition: "all 0.3s ease",
                            boxShadow: `0 4px 10px ${card.fillColor}20`
                          }}
                        >
                          {React.cloneElement(card.icon, { sx: { fontSize: 22 } })}
                        </Box>

                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            color: "text.primary",
                            fontSize: "1.1rem",
                            mb: 0.5,
                          }}
                        >
                          {card.value}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            lineHeight: 1.2,
                            textTransform: 'uppercase',
                            opacity: 0.6
                          }}
                        >
                          {card.title}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            </motion.div>

            {/* Monthly Analytics Charts */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >

              <Box
                sx={{
                  mb: { xs: 3, md: 4 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                {/* Orders Chart - Now Full Width */}
                <Box>
                  <Card
                    sx={{
                      background: "linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)",
                      border: "1px solid rgba(0, 0, 0, 0.06)",
                      borderRadius: { xs: 2, md: 3 },
                      overflow: "hidden",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 3,
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "text.primary",
                            fontSize: { xs: "1rem", md: "1.1rem" },
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <ShoppingCartIcon sx={{ color: "#00BCD4", fontSize: 20 }} />
                          Orders Velocity
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            p: "3px",
                            borderRadius: "8px",
                            border: "1px solid rgba(0, 0, 0, 0.06)",
                          }}
                        >
                          {availableYears.map((year) => {
                            const isActive = selectedYear === year;
                            return (
                              <Button
                                key={year}
                                size="small"
                                onClick={() => handleYearChange(year)}
                                sx={{
                                  minWidth: "52px",
                                  px: 1.5,
                                  py: 0.3,
                                  fontSize: "0.75rem",
                                  fontWeight: isActive ? 700 : 500,
                                  textTransform: "none",
                                  borderRadius: "6px",
                                  color: isActive ? "#FFFFFF" : "text.secondary",
                                  backgroundColor: isActive ? "#00BCD4" : "transparent",
                                  boxShadow: isActive ? "0px 2px 4px rgba(0, 188, 212, 0.3)" : "none",
                                  "&:hover": {
                                    backgroundColor: isActive
                                      ? "#00ACC1"
                                      : "rgba(0, 0, 0, 0.08)",
                                  },
                                }}
                              >
                                {year}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>
                      {monthlyData.length > 0 ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: { xs: 280, sm: 320, md: 380, lg: 400 },
                            overflowX: "auto",
                          }}
                        >
                          <BarChart
                            dataset={monthlyData}
                            xAxis={[
                              {
                                scaleType: "band",
                                dataKey: "month",
                                label: "Month",
                                labelStyle: {
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 600,
                                },
                              },
                            ]}
                            yAxis={[
                              {
                                label: "Number of Orders",
                                labelStyle: {
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 600,
                                },
                                tickFormatter: (value) => `${value}`,
                              },
                            ]}
                            series={[
                              {
                                dataKey: "orders",
                                label: "Total",
                                color: "#00BCD4"
                              },
                              {
                                dataKey: "delivered",
                                label: "Delivered",
                                color: "#10B981",
                              },
                              {
                                dataKey: "canceled",
                                label: "Cancelled",
                                color: "#EF4444",
                              },
                            ]}
                            width={undefined}
                            height={undefined}
                            sx={{
                              "& .MuiChartsAxis-root": {
                                "& .MuiChartsAxis-tickLabel": {
                                  fontSize: { xs: 9, md: 11 },
                                  fontWeight: 500,
                                },
                              },
                              "& .MuiChartsLegend-root": {
                                "& .MuiChartsLegend-series": {
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 600,
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: { xs: 280, md: 300 },
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={40} sx={{ color: "primary.main" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                            }}
                          >
                            Loading chart data...
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </motion.div>

            {/* Monthly Expenses & Orders by Weekday Charts */}
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, 1fr)",
                  },
                  gap: { xs: 2, md: 3 },
                  px: { xs: 1, sm: 0 },
                }}
              >
                {/* Monthly Expenses Chart - 50% */}
                <Box sx={{ display: 'flex' }}>
                  <Card
                    sx={{
                      background: "linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)",
                      border: "1px solid rgba(0, 0, 0, 0.06)",
                      borderRadius: { xs: 2, md: 3 },
                      overflow: "hidden",
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          mb: 3,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <ReceiptIcon sx={{ color: '#EF4444', fontSize: 20 }} />
                        Expenses Breakdown
                      </Typography>
                      {expenseData.length > 0 ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: { xs: 300, sm: 350, md: 400, lg: 450 },
                            overflowX: "auto",
                          }}
                        >
                          <BarChart
                            dataset={expenseData}
                            xAxis={[
                              {
                                scaleType: "band",
                                dataKey: "month",
                                label: "Month",
                                labelStyle: {
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 600,
                                },
                                categoryGapRatio: 0.5,
                                barGapRatio: 0.1,
                              },
                            ]}
                            yAxis={[
                              {
                                label: "Amount (₹)",
                                labelStyle: {
                                  fontSize: { xs: 10, md: 12 },
                                  fontWeight: 600,
                                },
                                tickFormatter: (value) => `₹${(value / 1000).toFixed(0)}k`,
                              },
                            ]}
                            series={[
                              {
                                dataKey: "ads",
                                label: "Ads",
                                color: "#6C63FF",
                                stack: "other",
                              },
                              {
                                dataKey: "packaging",
                                label: "Packaging",
                                color: "#10B981",
                                stack: "other",
                              },
                              {
                                dataKey: "material",
                                label: "Material",
                                color: "#F59E0B",
                                stack: "other",
                              },
                              {
                                dataKey: "shipping",
                                label: "Shipping",
                                color: "#B51D6E",
                                stack: "other",
                              },
                              {
                                dataKey: "total",
                                label: "Total",
                                color: "#EF4444",
                                stack: "total",
                              },
                            ]}
                            width={undefined}
                            height={undefined}
                            sx={{
                              "& .MuiChartsAxis-root": {
                                "& .MuiChartsAxis-tickLabel": {
                                  fontSize: { xs: 9, md: 11 },
                                  fontWeight: 500,
                                },
                              },
                              "& .MuiChartsLegend-root": {
                                "& .MuiChartsLegend-series": {
                                  fontSize: { xs: 9, md: 11 },
                                  fontWeight: 600,
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: { xs: 300, md: 400 },
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={40} sx={{ color: "primary.main" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                            }}
                          >
                            Loading expense data...
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>

                {/* Orders by Weekday Chart - 50% */}
                <Box sx={{ display: 'flex' }}>
                  <Card
                    sx={{
                      background: "linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)",
                      border: "1px solid rgba(0, 0, 0, 0.06)",
                      borderRadius: { xs: 2, md: 3 },
                      overflow: "hidden",
                      height: "100%",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "text.primary",
                          mb: 3,
                          fontSize: { xs: "1rem", md: "1.1rem" },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <HistoryIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
                        Orders by Weekday
                      </Typography>
                      {weekdayData.length > 0 ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: { xs: 300, sm: 350, md: 400, lg: 450 },
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <PieChart
                            series={[
                              {
                                data: weekdayData
                                  .filter(item => item.order_count > 0)
                                  .map((item, index) => ({
                                    id: index,
                                    value: Number(item.order_count) || 0,
                                    label: String(item.weekday || ''),
                                  })),
                                innerRadius: isMobile ? 30 : 40,
                                outerRadius: isMobile ? 100 : 120,
                                paddingAngle: 2,
                                cornerRadius: 5,
                              },
                            ]}
                            width={undefined}
                            height={undefined}
                            sx={{
                              width: "100%",
                              height: "100%",
                              "& .MuiChartsLegend-root": {
                                "& .MuiChartsLegend-series": {
                                  fontSize: { xs: 9, md: 11 },
                                  fontWeight: 600,
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: { xs: 300, md: 400 },
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={40} sx={{ color: "primary.main" }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              fontSize: { xs: "0.75rem", md: "0.875rem" },
                            }}
                          >
                            Loading weekday data...
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </motion.div>
          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default Dashboard;
